import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Search, Package, Pencil, Trash2, Upload, Image as ImageIcon, TrendingUp, Send, RotateCcw, Warehouse, ShoppingCart, AlertTriangle, FileText, Download, Check, X, History, ClipboardList, Trophy } from "lucide-react";
import RankingProdutosPage from "./RankingProdutosPage";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ProdutosPage() {
  const [produtos, setProdutos] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduto, setEditingProduto] = useState(null);
  const [activeTab, setActiveTab] = useState("geral");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [produtoToDelete, setProdutoToDelete] = useState(null);

  const [formData, setFormData] = useState({
    nome: "",
    codigo_sku: "",
    categoria: "",
    preco_venda: "",
    preco_cliente_final: "",
    preco_custo: "",
    ponto_pedido: "",
    estoque_maximo: "",
    icms_percent: "17",
    frete_percent: "25",
    custo_operacional_percent: "40"
  });

  const [categoriasLoja, setCategoriasLoja] = useState([]);
  const [showNovaCategoria, setShowNovaCategoria] = useState(false);
  const [novaCategoriaNome, setNovaCategoriaNome] = useState("");

  // Saldos completos por produto: {produto_id: {saldo_loja, carros:[{carro_id,carro_nome,quantidade}]}}
  const [saldosMap, setSaldosMap] = useState({});
  const [carros, setCarros] = useState([]);

  // ===== Pedidos de Reposição (orçamentos) =====
  const [tabPagina, setTabPagina] = useState("catalogo"); // 'catalogo' | 'alertas' | 'pedidos'
  const [alertasBaixo, setAlertasBaixo] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [carrinhoOrcamento, setCarrinhoOrcamento] = useState([]); // [{produto_id, produto_nome, quantidade}]
  const [carrinhoOpen, setCarrinhoOpen] = useState(false);
  const [dataEntradaPrevista, setDataEntradaPrevista] = useState("");
  const [obsOrcamento, setObsOrcamento] = useState("");
  const [fechandoOrcamento, setFechandoOrcamento] = useState(false);
  const [downloadingPdfAlertas, setDownloadingPdfAlertas] = useState(false);

  // Ações rápidas direto do card (page-level dialogs)
  const [acaoProduto, setAcaoProduto] = useState(null); // produto sob ação
  const [acaoTipo, setAcaoTipo] = useState(null); // 'entrada' | 'transferir' | 'devolver'
  const [acaoSubmitting, setAcaoSubmitting] = useState(false);
  const [formEntradaQuick, setFormEntradaQuick] = useState({ quantidade: "", motivo: "" });
  const [formTransferQuick, setFormTransferQuick] = useState({ quantidade: "", carro_id: "" });
  const [formDevolverQuick, setFormDevolverQuick] = useState({ quantidade: "", carro_id: "", motivo: "" });

  useEffect(() => { loadProdutos(); loadSaldosTodos(); }, [statusFilter]);
  useEffect(() => { loadCategoriasLoja(); loadCarros(); loadAlertas(); loadPedidos(); }, []);

  const loadSaldosTodos = async () => {
    try {
      const { data } = await axios.get(`${API}/estoques/saldos-todos`);
      setSaldosMap(data || {});
    } catch {}
  };

  const loadCarros = async () => {
    try { const { data } = await axios.get(`${API}/carros`); setCarros(data || []); } catch {}
  };

  const loadAlertas = async () => {
    try {
      const { data } = await axios.get(`${API}/estoques/alertas/loja`);
      setAlertasBaixo(data || []);
    } catch {}
  };

  const loadPedidos = async () => {
    try {
      const { data } = await axios.get(`${API}/pedidos-reposicao`);
      setPedidos(data || []);
    } catch {}
  };

  // Mapa produto_id -> pedido aprovado (se houver). Habilita botão "Entrada Hoje".
  const pedidoAprovadoPorProduto = (() => {
    const map = {};
    pedidos.filter(p => p.status === "aprovado").forEach(p => {
      (p.itens || []).forEach(it => { map[it.produto_id] = p; });
    });
    return map;
  })();
  // Mapa produto_id -> pedido pendente (mostrar badge "no carrinho/orçamento aberto")
  const pedidoPendentePorProduto = (() => {
    const map = {};
    pedidos.filter(p => p.status === "pendente").forEach(p => {
      (p.itens || []).forEach(it => { map[it.produto_id] = p; });
    });
    return map;
  })();

  const sugestaoPedido = (produto) => {
    const max = parseInt(produto.estoque_maximo || 0);
    const saldo = saldoLojaDe(produto.id);
    return max > 0 && saldo >= max ? 1 : Math.max(1, max - saldo);
  };

  const adicionarAoCarrinho = (produto) => {
    setCarrinhoOrcamento(prev => {
      if (prev.find(it => it.produto_id === produto.id)) return prev; // já no carrinho
      return [...prev, {
        produto_id: produto.id,
        produto_nome: produto.nome,
        produto_codigo: produto.codigo_sku,
        preco_custo: produto.preco_custo,
        quantidade: sugestaoPedido(produto),
      }];
    });
    toast.success(`${produto.nome} adicionado ao orçamento`);
  };

  const removerDoCarrinho = (produtoId) => {
    setCarrinhoOrcamento(prev => prev.filter(it => it.produto_id !== produtoId));
  };

  const atualizarQtdCarrinho = (produtoId, qtd) => {
    const n = Math.max(0, parseInt(qtd) || 0);
    setCarrinhoOrcamento(prev => prev.map(it => it.produto_id === produtoId ? { ...it, quantidade: n } : it));
  };

  const fecharOrcamento = async () => {
    if (carrinhoOrcamento.length === 0) { toast.error("Adicione itens primeiro"); return; }
    if (!dataEntradaPrevista) { toast.error("Defina a data de entrada prevista"); return; }
    const valid = carrinhoOrcamento.filter(it => it.quantidade > 0);
    if (valid.length === 0) { toast.error("Todos os itens têm quantidade 0"); return; }
    setFechandoOrcamento(true);
    try {
      const { data } = await axios.post(`${API}/pedidos-reposicao`, {
        data_entrada_prevista: dataEntradaPrevista,
        observacoes: obsOrcamento,
        itens: valid.map(it => ({ produto_id: it.produto_id, quantidade: it.quantidade })),
      });
      toast.success(`Orçamento ${data.numero} criado!`);
      setCarrinhoOrcamento([]);
      setCarrinhoOpen(false);
      setDataEntradaPrevista("");
      setObsOrcamento("");
      await loadPedidos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao fechar orçamento");
    } finally { setFechandoOrcamento(false); }
  };

  const aprovarPedido = async (pedidoId, payload = {}) => {
    try {
      await axios.post(`${API}/pedidos-reposicao/${pedidoId}/aprovar`, payload);
      toast.success("Pedido aprovado!");
      await loadPedidos();
      return true;
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao aprovar"); return false; }
  };

  const editarPedido = async (pedidoId, itens) => {
    try {
      await axios.put(`${API}/pedidos-reposicao/${pedidoId}`, { itens });
      toast.success("Pedido atualizado!");
      await loadPedidos();
      return true;
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao editar"); return false; }
  };

  const cancelarPedido = async (pedidoId) => {
    if (!window.confirm("Cancelar este pedido?")) return;
    try {
      await axios.delete(`${API}/pedidos-reposicao/${pedidoId}`);
      toast.success("Pedido cancelado");
      await loadPedidos();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao cancelar"); }
  };

  const darEntradaPedido = async (pedido) => {
    try {
      await axios.post(`${API}/pedidos-reposicao/${pedido.id}/dar-entrada`);
      toast.success(`Entrada do pedido ${pedido.numero} aplicada!`);
      // Atualiza saldos otimisticamente
      (pedido.itens || []).forEach(it => aplicarEntradaLocal(it.produto_id, parseInt(it.quantidade || 0)));
      await loadPedidos();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao dar entrada"); }
  };

  const baixarPdfPedido = async (pedido) => {
    try {
      const response = await axios.get(`${API}/pedidos-reposicao/${pedido.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `pedido_${pedido.numero}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF gerado!");
    } catch { toast.error("Erro ao gerar PDF"); }
  };

  const handleDownloadPDFAlertas = async () => {
    setDownloadingPdfAlertas(true);
    try {
      const response = await axios.get(`${API}/estoques/alertas/relatorio-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `relatorio_estoque_baixo_${new Date().toISOString().split("T")[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF baixado!");
    } catch { toast.error("Erro ao baixar PDF"); }
    finally { setDownloadingPdfAlertas(false); }
  };

  const saldoLojaDe = (pid) => saldosMap[pid]?.saldo_loja ?? 0;
  const carrosDe = (pid) => saldosMap[pid]?.carros ?? [];
  const carrosComSaldoDe = (pid) => carrosDe(pid).filter(c => c.quantidade > 0);

  const recarregarTudo = async () => {
    await loadSaldosTodos(); // só atualiza saldos, NÃO reseta a lista de produtos (preserva scroll)
  };

  // Atualizações otimistas — evitam refresh completo da lista
  const aplicarEntradaLocal = (produtoId, qtd) => {
    setSaldosMap(prev => {
      const cur = prev[produtoId] || { saldo_loja: 0, carros: [] };
      return { ...prev, [produtoId]: { ...cur, saldo_loja: cur.saldo_loja + qtd } };
    });
  };
  const aplicarTransferenciaLocal = (produtoId, qtd, carroId) => {
    setSaldosMap(prev => {
      const cur = prev[produtoId] || { saldo_loja: 0, carros: [] };
      const carros = cur.carros.map(c => c.carro_id === carroId ? { ...c, quantidade: c.quantidade + qtd } : c);
      return { ...prev, [produtoId]: { ...cur, saldo_loja: cur.saldo_loja - qtd, carros } };
    });
  };
  const aplicarDevolucaoLocal = (produtoId, qtd, carroId) => {
    setSaldosMap(prev => {
      const cur = prev[produtoId] || { saldo_loja: 0, carros: [] };
      const carros = cur.carros.map(c => c.carro_id === carroId ? { ...c, quantidade: c.quantidade - qtd } : c);
      return { ...prev, [produtoId]: { ...cur, saldo_loja: cur.saldo_loja + qtd, carros } };
    });
  };

  const abrirAcao = (produto, tipo) => {
    setAcaoProduto(produto);
    setAcaoTipo(tipo);
    setFormEntradaQuick({ quantidade: "", motivo: "" });
    setFormTransferQuick({ quantidade: "", carro_id: "" });
    setFormDevolverQuick({ quantidade: "", carro_id: "", motivo: "" });
  };

  const fecharAcao = () => { setAcaoProduto(null); setAcaoTipo(null); };

  const submitEntradaQuick = async (e) => {
    e.preventDefault();
    setAcaoSubmitting(true);
    const qtd = parseInt(formEntradaQuick.quantidade);
    try {
      await axios.post(`${API}/movimentacoes`, {
        produto_id: acaoProduto.id,
        tipo: "entrada_loja",
        quantidade: qtd,
        motivo: formEntradaQuick.motivo || "Reposição",
      });
      toast.success("Entrada registrada!");
      aplicarEntradaLocal(acaoProduto.id, qtd);
      fecharAcao();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao registrar entrada"); }
    finally { setAcaoSubmitting(false); }
  };

  const submitTransferQuick = async (e) => {
    e.preventDefault();
    setAcaoSubmitting(true);
    const qtd = parseInt(formTransferQuick.quantidade);
    const carroId = formTransferQuick.carro_id;
    try {
      await axios.post(`${API}/movimentacoes`, {
        produto_id: acaoProduto.id,
        tipo: "transferencia",
        quantidade: qtd,
        carro_id_destino: carroId,
        motivo: "Transferência para carro",
      });
      toast.success("Transferência realizada!");
      aplicarTransferenciaLocal(acaoProduto.id, qtd, carroId);
      fecharAcao();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao transferir"); }
    finally { setAcaoSubmitting(false); }
  };

  const submitDevolverQuick = async (e) => {
    e.preventDefault();
    setAcaoSubmitting(true);
    const qtd = parseInt(formDevolverQuick.quantidade);
    const carroId = formDevolverQuick.carro_id;
    try {
      await axios.post(`${API}/movimentacoes`, {
        produto_id: acaoProduto.id,
        tipo: "devolucao_carro",
        quantidade: qtd,
        carro_id_origem: carroId,
        motivo: formDevolverQuick.motivo || "Devolução do carro para loja",
      });
      toast.success("Devolução realizada!");
      aplicarDevolucaoLocal(acaoProduto.id, qtd, carroId);
      fecharAcao();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao devolver"); }
    finally { setAcaoSubmitting(false); }
  };

  const maxDevolverCarro = (carroId) => {
    if (!acaoProduto) return 0;
    return carrosDe(acaoProduto.id).find(c => c.carro_id === carroId)?.quantidade || 0;
  };

  const loadCategoriasLoja = async () => {
    try { const { data } = await axios.get(`${API}/loja/categorias`); setCategoriasLoja(data); } catch {}
  };

  const criarCategoria = async () => {
    const nome = novaCategoriaNome.trim();
    if (!nome) return;
    try {
      const { data } = await axios.post(`${API}/loja/categorias`, { nome });
      toast.success("Categoria criada!");
      setCategoriasLoja([...categoriasLoja, data]);
      setFormData({ ...formData, categoria: data.nome });
      setShowNovaCategoria(false);
      setNovaCategoriaNome("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const loadProdutos = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "todos") params.append("status", statusFilter);
      if (searchQuery) params.append("busca", searchQuery);
      const response = await axios.get(`${API}/produtos?${params}`);
      setProdutos(response.data);
    } catch (error) {
      toast.error("Erro ao carregar produtos");
    }
  };

  const handleSearch = () => { loadProdutos(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        preco_venda: parseFloat(formData.preco_venda),
        preco_cliente_final: formData.preco_cliente_final !== "" && formData.preco_cliente_final != null ? parseFloat(formData.preco_cliente_final) : null,
        preco_custo: parseFloat(formData.preco_custo),
        ponto_pedido: parseInt(formData.ponto_pedido),
        estoque_maximo: parseInt(formData.estoque_maximo),
        icms_percent: parseFloat(formData.icms_percent) || 17,
        frete_percent: parseFloat(formData.frete_percent) || 25,
        custo_operacional_percent: parseFloat(formData.custo_operacional_percent) || 40
      };
      if (editingProduto) {
        const { data: updated } = await axios.put(`${API}/produtos/${editingProduto.id}`, data);
        toast.success("Produto atualizado!");
        // ATUALIZA IN-PLACE — preserva scroll do usuário
        setProdutos(prev => prev.map(p => p.id === updated.id ? updated : p));
        // Fecha o modal automaticamente (UX)
        setDialogOpen(false);
        resetForm();
      } else {
        const { data: criado } = await axios.post(`${API}/produtos`, data);
        toast.success("Produto cadastrado!");
        setDialogOpen(false);
        resetForm();
        // Adiciona o novo no topo da lista sem recarregar tudo
        if (criado?.id) setProdutos(prev => [criado, ...prev]);
        else loadProdutos();
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao salvar produto";
      toast.error(message);
    }
  };

  const handleEdit = (produto) => {
    setEditingProduto(produto);
    setActiveTab("geral");
    setFormData({
      nome: produto.nome,
      codigo_sku: produto.codigo_sku,
      categoria: produto.categoria || "",
      preco_venda: produto.preco_venda.toString(),
      preco_cliente_final: produto.preco_cliente_final != null ? produto.preco_cliente_final.toString() : "",
      preco_custo: produto.preco_custo.toString(),
      ponto_pedido: produto.ponto_pedido.toString(),
      estoque_maximo: produto.estoque_maximo.toString(),
      icms_percent: (produto.icms_percent != null ? produto.icms_percent : 17).toString(),
      frete_percent: (produto.frete_percent != null ? produto.frete_percent : 25).toString(),
      custo_operacional_percent: (produto.custo_operacional_percent != null ? produto.custo_operacional_percent : 40).toString()
    });
    setDialogOpen(true);
  };

  const handleDeleteClick = (produto) => {
    setProdutoToDelete(produto);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${API}/produtos/${produtoToDelete.id}`);
      toast.success("Produto excluído!");
      setDeleteDialogOpen(false);
      setProdutoToDelete(null);
      loadProdutos();
    } catch (error) {
      toast.error("Erro ao excluir produto");
    }
  };

  const resetForm = () => {
    setFormData({
      nome: "", codigo_sku: "", categoria: "", preco_venda: "", preco_cliente_final: "",
      preco_custo: "", ponto_pedido: "", estoque_maximo: "",
      icms_percent: "17", frete_percent: "25", custo_operacional_percent: "40"
    });
    setEditingProduto(null);
    setActiveTab("geral");
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) resetForm();
  };

  const refreshProdutoEdicao = async () => {
    if (!editingProduto?.id) return;
    try {
      const { data } = await axios.get(`${API}/produtos/${editingProduto.id}`);
      setEditingProduto(data);
    } catch {}
    loadProdutos();
    loadSaldosTodos();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold" style={{ fontFamily: 'Work Sans' }}>Gestão de Produtos</h2>
        <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-blue-500 hover:bg-blue-600" data-testid="btn-novo-produto">
              <Plus className="h-4 w-4" /> Novo Produto
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProduto ? `Editar — ${editingProduto.nome}` : "Cadastrar Novo Produto"}</DialogTitle>
            </DialogHeader>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className={`grid w-full ${editingProduto ? 'grid-cols-3' : 'grid-cols-1'}`}>
                <TabsTrigger value="geral" data-testid="tab-geral">Geral</TabsTrigger>
                {editingProduto && <TabsTrigger value="loja" data-testid="tab-loja">Loja Online</TabsTrigger>}
                {editingProduto && <TabsTrigger value="estoque" data-testid="tab-estoque">Estoque & Movimentação</TabsTrigger>}
              </TabsList>

              {/* ===== ABA GERAL ===== */}
              <TabsContent value="geral" className="mt-4">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="nome">Nome do Produto *</Label>
                      <Input id="nome" required value={formData.nome}
                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                        placeholder="Ex: Notebook Dell Inspiron" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="codigo_sku">Código/SKU *</Label>
                      <Input id="codigo_sku" required value={formData.codigo_sku}
                        onChange={(e) => setFormData({ ...formData, codigo_sku: e.target.value })}
                        placeholder="Ex: PROD-001" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="categoria">Categoria</Label>
                      <div className="flex gap-2">
                        <select id="categoria" value={formData.categoria}
                          onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          data-testid="produto-categoria-select">
                          <option value="">-- Selecione --</option>
                          {categoriasLoja.map(c => <option key={c.id} value={c.nome}>{c.nome}</option>)}
                        </select>
                        <Button type="button" variant="outline" onClick={() => setShowNovaCategoria(true)} className="gap-1" data-testid="btn-nova-categoria">
                          <Plus className="h-4 w-4" /> Nova
                        </Button>
                      </div>
                      {showNovaCategoria && (
                        <div className="flex gap-2 mt-2">
                          <Input autoFocus placeholder="Nome da categoria (ex: PERFUMES)" value={novaCategoriaNome}
                            onChange={e => setNovaCategoriaNome(e.target.value.toUpperCase())}
                            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), criarCategoria())}
                            data-testid="input-nova-categoria-produto" />
                          <Button type="button" onClick={criarCategoria} className="bg-fuchsia-600 hover:bg-fuchsia-700">Criar</Button>
                          <Button type="button" variant="ghost" onClick={() => { setShowNovaCategoria(false); setNovaCategoriaNome(""); }}>×</Button>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="preco_custo">Preço de Custo (R$) *</Label>
                      <Input id="preco_custo" type="number" step="0.01" required value={formData.preco_custo}
                        onChange={(e) => setFormData({ ...formData, preco_custo: e.target.value })}
                        placeholder="0.00" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="preco_venda">Preço SALÃO/REVENDA (R$) *</Label>
                      <Input id="preco_venda" type="number" step="0.01" required value={formData.preco_venda}
                        onChange={(e) => setFormData({ ...formData, preco_venda: e.target.value })}
                        placeholder="0.00" className="border-purple-300" data-testid="input-preco-venda" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="preco_cliente_final">Preço CLIENTE FINAL (R$)</Label>
                      <Input id="preco_cliente_final" type="number" step="0.01" value={formData.preco_cliente_final ?? ""}
                        onChange={(e) => setFormData({ ...formData, preco_cliente_final: e.target.value })}
                        placeholder="Opcional — vazio usa o salão" className="border-pink-300" data-testid="input-preco-cliente-final" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ponto_pedido">Estoque Mínimo (Alerta) *</Label>
                      <Input id="ponto_pedido" type="number" required value={formData.ponto_pedido}
                        onChange={(e) => setFormData({ ...formData, ponto_pedido: e.target.value })}
                        placeholder="Ex: 10" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="estoque_maximo">Estoque Máximo (Ideal) *</Label>
                      <Input id="estoque_maximo" type="number" required value={formData.estoque_maximo}
                        onChange={(e) => setFormData({ ...formData, estoque_maximo: e.target.value })}
                        placeholder="Ex: 100" />
                    </div>
                  </div>

                  {/* Custos e Markup */}
                  {formData.preco_custo && parseFloat(formData.preco_custo) > 0 && (() => {
                    const custo = parseFloat(formData.preco_custo) || 0;
                    const venda = parseFloat(formData.preco_venda) || 0;
                    const icmsP = parseFloat(formData.icms_percent) || 0;
                    const freteP = parseFloat(formData.frete_percent) || 0;
                    const custOpP = parseFloat(formData.custo_operacional_percent) || 0;
                    const icmsVal = custo * icmsP / 100;
                    const freteVal = custo * freteP / 100;
                    const custOpVal = custo * custOpP / 100;
                    const cmv = custo + icmsVal + freteVal + custOpVal;
                    const margem = venda - cmv;
                    const markupPercent = cmv > 0 ? ((venda - cmv) / cmv * 100) : 0;
                    return (
                      <div className="bg-gray-50 border rounded-lg p-4 space-y-3">
                        <p className="text-sm font-bold text-gray-700">Composição de Custo</p>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">ICMS (%)</Label>
                            <Input type="number" step="0.1" min="0" value={formData.icms_percent}
                              onChange={(e) => setFormData({ ...formData, icms_percent: e.target.value })}
                              className="h-8 text-sm" data-testid="icms-percent-input" />
                            <p className="text-xs text-gray-500">R$ {icmsVal.toFixed(2)}</p>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Frete (%)</Label>
                            <Input type="number" step="0.1" min="0" value={formData.frete_percent}
                              onChange={(e) => setFormData({ ...formData, frete_percent: e.target.value })}
                              className="h-8 text-sm" data-testid="frete-percent-input" />
                            <p className="text-xs text-gray-500">R$ {freteVal.toFixed(2)}</p>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Custo Op. (%)</Label>
                            <Input type="number" step="0.1" min="0" value={formData.custo_operacional_percent}
                              onChange={(e) => setFormData({ ...formData, custo_operacional_percent: e.target.value })}
                              className="h-8 text-sm" data-testid="custo-op-percent-input" />
                            <p className="text-xs text-gray-500">R$ {custOpVal.toFixed(2)}</p>
                          </div>
                        </div>
                        <div className="border-t pt-3 grid grid-cols-3 gap-3">
                          <div className="bg-amber-50 rounded p-2 text-center">
                            <p className="text-xs text-amber-700 font-semibold">CMV</p>
                            <p className="text-lg font-bold text-amber-800">R$ {cmv.toFixed(2)}</p>
                          </div>
                          <div className={`rounded p-2 text-center ${markupPercent >= 0 ? 'bg-blue-50' : 'bg-red-50'}`}>
                            <p className={`text-xs font-semibold ${markupPercent >= 0 ? 'text-blue-700' : 'text-red-700'}`}>Markup</p>
                            <p className={`text-lg font-bold ${markupPercent >= 0 ? 'text-blue-800' : 'text-red-800'}`}>{markupPercent.toFixed(1)}%</p>
                          </div>
                          <div className={`rounded p-2 text-center ${margem >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                            <p className={`text-xs font-semibold ${margem >= 0 ? 'text-green-700' : 'text-red-700'}`}>Margem</p>
                            <p className={`text-lg font-bold ${margem >= 0 ? 'text-green-800' : 'text-red-800'}`}>R$ {margem.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => handleDialogClose(false)}>Cancelar</Button>
                    <Button type="submit" className="bg-blue-500 hover:bg-blue-600" data-testid="btn-salvar-produto">
                      {editingProduto ? "Salvar Alterações" : "Cadastrar"}
                    </Button>
                  </div>
                </form>
              </TabsContent>

              {/* ===== ABA LOJA ONLINE ===== */}
              {editingProduto && (
                <TabsContent value="loja" className="mt-4">
                  <LojaTab produto={editingProduto} onChanged={refreshProdutoEdicao} />
                </TabsContent>
              )}

              {/* ===== ABA ESTOQUE ===== */}
              {editingProduto && (
                <TabsContent value="estoque" className="mt-4">
                  <EstoqueTab produto={editingProduto} onChanged={refreshProdutoEdicao} />
                </TabsContent>
              )}
            </Tabs>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs principais: Catálogo / Alertas / Pedidos / Ranking */}
      <Tabs value={tabPagina} onValueChange={setTabPagina}>
        <TabsList className="grid w-full max-w-3xl grid-cols-4">
          <TabsTrigger value="catalogo" data-testid="page-tab-catalogo">
            <Package className="h-4 w-4 mr-1.5" /> Catálogo
          </TabsTrigger>
          <TabsTrigger value="alertas" data-testid="page-tab-alertas">
            <AlertTriangle className="h-4 w-4 mr-1.5" /> Alertas
            {alertasBaixo.length > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] bg-red-500 text-white font-bold">{alertasBaixo.length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="pedidos" data-testid="page-tab-pedidos">
            <ClipboardList className="h-4 w-4 mr-1.5" /> Pedidos
            {pedidos.filter(p => p.status === "pendente" || p.status === "aprovado").length > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] bg-amber-500 text-white font-bold">
                {pedidos.filter(p => p.status === "pendente" || p.status === "aprovado").length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="ranking" data-testid="page-tab-ranking">
            <Trophy className="h-4 w-4 mr-1.5" /> Ranking do Mês
          </TabsTrigger>
        </TabsList>

        <TabsContent value="catalogo" className="mt-4 space-y-6">
          {/* Filtros */}
          <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label htmlFor="search">Buscar por nome ou código</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input id="search" placeholder="Digite para buscar..." className="pl-10" value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()} />
              </div>
            </div>
            <div className="w-48">
              <Label htmlFor="status">Status do Estoque</Label>
              <select id="status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md">
                <option value="todos">Todos</option>
                <option value="normal">Estoque Normal</option>
                <option value="baixo">Estoque Baixo</option>
              </select>
            </div>
            <Button onClick={handleSearch} className="bg-blue-500 hover:bg-blue-600">Buscar</Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Produtos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {produtos.map((produto) => (
          <Card key={produto.id} className="hover:shadow-md transition-shadow" data-testid={`card-produto-${produto.id}`}>
            <CardHeader className="pb-3">
              <div className="flex gap-3 items-start">
                {/* Miniatura */}
                <div className="w-14 h-14 rounded-md bg-gray-100 border flex items-center justify-center overflow-hidden flex-shrink-0">
                  {produto.imagem_url ? (
                    <img src={produto.imagem_url} alt={produto.nome} className="w-full h-full object-cover" />
                  ) : (
                    <Package className="h-6 w-6 text-gray-300" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-base leading-tight truncate">{produto.nome}</CardTitle>
                  <p className="text-xs text-gray-500 mt-0.5">SKU: {produto.codigo_sku}</p>
                  {produto.categoria && (<p className="text-[11px] text-gray-400 mt-0.5">{produto.categoria}</p>)}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(produto)} className="h-8 w-8 p-0" data-testid={`btn-editar-${produto.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(produto)} className="h-8 w-8 p-0 text-red-600 hover:text-red-700" data-testid={`btn-excluir-${produto.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><p className="text-gray-600">Estoque Mínimo:</p><p className="font-medium">{produto.ponto_pedido}</p></div>
                <div><p className="text-gray-600">Estoque Máximo:</p><p className="font-medium">{produto.estoque_maximo}</p></div>
              </div>
              <div className="pt-2 border-t">
                <div className="flex justify-between text-sm"><span className="text-gray-600">Custo:</span><span>R$ {produto.preco_custo.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm font-medium"><span className="text-gray-600">Salão/Revenda:</span><span className="text-green-600">R$ {produto.preco_venda.toFixed(2)}</span></div>
                {produto.preco_cliente_final != null && (
                  <div className="flex justify-between text-sm font-medium"><span className="text-pink-700">Cliente Final:</span><span className="text-pink-700">R$ {Number(produto.preco_cliente_final).toFixed(2)}</span></div>
                )}
                {(() => {
                  const c = produto.preco_custo;
                  const v = produto.preco_venda;
                  const icms = c * (produto.icms_percent ?? 17) / 100;
                  const frete = c * (produto.frete_percent ?? 25) / 100;
                  const custOp = c * (produto.custo_operacional_percent ?? 40) / 100;
                  const cmv = c + icms + frete + custOp;
                  const margem = v - cmv;
                  const markup = cmv > 0 ? ((v - cmv) / cmv * 100) : 0;
                  return (
                    <div className="flex justify-between text-sm mt-2 gap-3">
                      <div className="flex gap-1"><span className="text-gray-600">Markup:</span><span className={`font-medium ${markup >= 0 ? 'text-blue-600' : 'text-red-600'}`}>{markup.toFixed(1)}%</span></div>
                      <div className="flex gap-1"><span className="text-gray-600">Margem:</span><span className={`font-medium ${margem >= 0 ? 'text-green-600' : 'text-red-600'}`}>R$ {margem.toFixed(2)}</span></div>
                    </div>
                  );
                })()}
              </div>

              {/* Estoque + Ações rápidas */}
              <div className="pt-3 border-t -mx-6 px-6 -mb-6 pb-4 bg-cyan-50/40 rounded-b">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5 text-xs text-cyan-800 font-semibold">
                    <Warehouse className="h-3.5 w-3.5" />
                    Saldo na Loja
                  </div>
                  <p className="text-lg font-bold text-cyan-900" data-testid={`saldo-loja-card-${produto.id}`}>
                    {saldoLojaDe(produto.id)}
                  </p>
                </div>
                {/* Saldos por carro */}
                {carrosDe(produto.id).length > 0 && (
                  <div className="mb-2.5">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Carros</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {carrosDe(produto.id).map(c => (
                        <div
                          key={c.carro_id}
                          className={`rounded px-1.5 py-1 text-center border ${c.quantidade > 0 ? 'bg-white border-indigo-200' : 'bg-gray-50 border-gray-200'}`}
                          data-testid={`card-saldo-carro-${produto.id}-${c.carro_id}`}
                          title={c.carro_nome}
                        >
                          <p className="text-[9px] text-gray-500 truncate leading-tight">{c.carro_nome}</p>
                          <p className={`text-sm font-bold leading-tight ${c.quantidade > 0 ? 'text-indigo-700' : 'text-gray-400'}`}>{c.quantidade}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => abrirAcao(produto, "entrada")} className="gap-1 text-emerald-700 border-emerald-300 hover:bg-emerald-50 text-xs px-2" data-testid={`card-btn-entrada-${produto.id}`}>
                    <TrendingUp className="h-3.5 w-3.5" /> Entrada
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => abrirAcao(produto, "transferir")} disabled={saldoLojaDe(produto.id) <= 0} className="gap-1 text-blue-700 border-blue-300 hover:bg-blue-50 text-xs px-2" data-testid={`card-btn-transferir-${produto.id}`}>
                    <Send className="h-3.5 w-3.5" /> Transferir
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => abrirAcao(produto, "devolver")} disabled={carrosComSaldoDe(produto.id).length === 0} className="gap-1 text-orange-700 border-orange-300 hover:bg-orange-50 text-xs px-2" data-testid={`card-btn-devolver-${produto.id}`}>
                    <RotateCcw className="h-3.5 w-3.5" /> Devolver
                  </Button>
                </div>
                {/* Botão NOVO PEDIDO / ENTRADA HOJE */}
                {(() => {
                  const pedAprovado = pedidoAprovadoPorProduto[produto.id];
                  const pedPendente = pedidoPendentePorProduto[produto.id];
                  const noCarrinho = carrinhoOrcamento.find(it => it.produto_id === produto.id);
                  if (pedAprovado) {
                    return (
                      <div className="mt-1.5 grid grid-cols-[1fr,auto] gap-1.5">
                        <Button
                          size="sm"
                          onClick={() => darEntradaPedido(pedAprovado)}
                          className="gap-1.5 bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`card-btn-entrada-hoje-${produto.id}`}
                        >
                          <Check className="h-4 w-4" /> ENTRADA HOJE — {pedAprovado.numero}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => baixarPdfPedido(pedAprovado)}
                          className="text-fuchsia-700 border-fuchsia-300 hover:bg-fuchsia-50 gap-1"
                          data-testid={`card-btn-pdf-pedido-${produto.id}`}
                          title="Imprimir PDF do pedido"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  }
                  if (pedPendente) {
                    return (
                      <div className="mt-1.5 grid grid-cols-[1fr,auto] gap-1.5">
                        <div className="text-center text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1.5 font-medium" data-testid={`card-badge-pedido-pendente-${produto.id}`}>
                          ⏳ Pedido {pedPendente.numero} pendente de aprovação
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => baixarPdfPedido(pedPendente)}
                          className="text-fuchsia-700 border-fuchsia-300 hover:bg-fuchsia-50 gap-1"
                          data-testid={`card-btn-pdf-pendente-${produto.id}`}
                          title="Imprimir PDF do pedido"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  }
                  if (noCarrinho) {
                    // Já no carrinho — controle inline de qtd (-/+)
                    return (
                      <div className="mt-1.5 flex items-center gap-1.5 border-2 border-fuchsia-300 bg-fuchsia-50 rounded-md p-1" data-testid={`card-pedido-inline-${produto.id}`}>
                        <Button size="sm" variant="ghost" onClick={() => atualizarQtdCarrinho(produto.id, Math.max(1, (noCarrinho.quantidade || 1) - 1))} className="h-7 w-7 p-0 text-fuchsia-700 hover:bg-fuchsia-100" data-testid={`card-pedido-minus-${produto.id}`}>−</Button>
                        <Input type="number" min="1" value={noCarrinho.quantidade}
                          onChange={e => atualizarQtdCarrinho(produto.id, Math.max(1, parseInt(e.target.value) || 1))}
                          className="h-7 text-center text-sm font-bold flex-1 px-1" data-testid={`card-pedido-qtd-${produto.id}`} />
                        <Button size="sm" variant="ghost" onClick={() => atualizarQtdCarrinho(produto.id, (noCarrinho.quantidade || 1) + 1)} className="h-7 w-7 p-0 text-fuchsia-700 hover:bg-fuchsia-100" data-testid={`card-pedido-plus-${produto.id}`}>+</Button>
                        <Button size="sm" variant="ghost" onClick={() => removerDoCarrinho(produto.id)} className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" title="Remover do orçamento" data-testid={`card-pedido-remove-${produto.id}`}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    );
                  }
                  const sug = sugestaoPedido(produto);
                  const saldoAtual = saldoLojaDe(produto.id);
                  const max = parseInt(produto.estoque_maximo || 0);
                  const acimaIdeal = max > 0 && saldoAtual >= max;
                  return (
                    <Button
                      size="sm"
                      onClick={() => adicionarAoCarrinho(produto)}
                      className="w-full mt-1.5 gap-1.5 bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                      data-testid={`card-btn-novo-pedido-${produto.id}`}
                    >
                      <ShoppingCart className="h-4 w-4" />
                      <span>Novo Pedido</span>
                      {!acimaIdeal && (<span className="text-[10px] opacity-80 font-normal">(sug. {sug})</span>)}
                    </Button>
                  );
                })()}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {produtos.length === 0 && (
        <div className="text-center py-12">
          <Package className="h-12 w-12 mx-auto text-gray-400" />
          <p className="text-gray-500 mt-4">Nenhum produto encontrado</p>
        </div>
      )}
        </TabsContent>

        {/* ===== Aba ALERTAS ===== */}
        <TabsContent value="alertas" className="mt-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Produtos com Estoque Baixo
              <span className="text-sm text-gray-500 font-normal">({alertasBaixo.length})</span>
            </h3>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={() => {
                  const idsNoCarrinho = new Set(carrinhoOrcamento.map(it => it.produto_id));
                  const idsBloqueados = new Set([
                    ...Object.keys(pedidoAprovadoPorProduto),
                    ...Object.keys(pedidoPendentePorProduto),
                  ]);
                  const novos = alertasBaixo
                    .map(a => produtos.find(p => p.id === a.produto_id))
                    .filter(p => p && !idsNoCarrinho.has(p.id) && !idsBloqueados.has(p.id));
                  if (novos.length === 0) { toast.info("Todos os alertas já estão no orçamento ou têm pedido em andamento"); return; }
                  setCarrinhoOrcamento(prev => [
                    ...prev,
                    ...novos.map(p => ({
                      produto_id: p.id,
                      produto_nome: p.nome,
                      produto_codigo: p.codigo_sku,
                      preco_custo: p.preco_custo,
                      quantidade: sugestaoPedido(p),
                    })),
                  ]);
                  toast.success(`${novos.length} produto(s) adicionado(s) ao orçamento`);
                }}
                disabled={alertasBaixo.length === 0}
                className="gap-2 bg-fuchsia-600 hover:bg-fuchsia-700"
                data-testid="btn-adicionar-todos-alertas"
              >
                <ShoppingCart className="h-4 w-4" /> Adicionar Todos ao Orçamento
              </Button>
              <Button onClick={handleDownloadPDFAlertas} disabled={downloadingPdfAlertas || alertasBaixo.length === 0} className="gap-2 bg-red-600 hover:bg-red-700" data-testid="btn-pdf-alertas">
                <Download className="h-4 w-4" /> {downloadingPdfAlertas ? "Gerando…" : "Gerar PDF"}
              </Button>
            </div>
          </div>
          {alertasBaixo.length === 0 ? (
            <div className="text-center py-12 bg-emerald-50 rounded-lg border border-emerald-200">
              <Package className="h-10 w-10 mx-auto text-emerald-500" />
              <p className="text-emerald-700 font-semibold mt-3">Tudo certo! Nenhum produto abaixo do estoque mínimo.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {alertasBaixo.map(a => {
                const prod = produtos.find(p => p.id === a.produto_id);
                return (
                  <Card key={a.produto_id} className="border-red-200" data-testid={`alerta-card-${a.produto_id}`}>
                    <CardContent className="pt-4 space-y-2">
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold truncate">{a.nome || prod?.nome}</p>
                          <p className="text-xs text-gray-500">SKU: {a.codigo_sku || prod?.codigo_sku}</p>
                        </div>
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 border border-red-200">BAIXO</span>
                      </div>
                      <div className="grid grid-cols-3 gap-1 text-center text-xs">
                        <div className="bg-red-50 rounded p-1.5">
                          <p className="text-[10px] text-red-700">Saldo</p>
                          <p className="font-bold text-red-700">{a.quantidade_atual}</p>
                        </div>
                        <div className="bg-amber-50 rounded p-1.5">
                          <p className="text-[10px] text-amber-700">Mínimo</p>
                          <p className="font-bold text-amber-700">{a.ponto_pedido}</p>
                        </div>
                        <div className="bg-emerald-50 rounded p-1.5">
                          <p className="text-[10px] text-emerald-700">Ideal</p>
                          <p className="font-bold text-emerald-700">{a.estoque_maximo}</p>
                        </div>
                      </div>
                      {prod && (
                        <Button size="sm" onClick={() => adicionarAoCarrinho(prod)} disabled={!!carrinhoOrcamento.find(it => it.produto_id === prod.id) || !!pedidoAprovadoPorProduto[prod.id] || !!pedidoPendentePorProduto[prod.id]} className="w-full gap-1.5 bg-fuchsia-600 hover:bg-fuchsia-700 text-white">
                          <ShoppingCart className="h-4 w-4" /> Adicionar ao Orçamento
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ===== Aba PEDIDOS ===== */}
        <TabsContent value="pedidos" className="mt-4 space-y-4">
          <PedidosTab pedidos={pedidos} onAprovar={aprovarPedido} onCancelar={cancelarPedido} onDarEntrada={darEntradaPedido} onEditar={editarPedido} />
        </TabsContent>

        {/* ===== Aba RANKING DO MÊS ===== */}
        <TabsContent value="ranking" className="mt-4">
          <RankingProdutosPage />
        </TabsContent>
      </Tabs>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o produto <strong>{produtoToDelete?.nome}</strong>?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialogs de ações rápidas (page-level) */}
      <Dialog open={acaoTipo === "entrada"} onOpenChange={(o) => !o && fecharAcao()}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Registrar Entrada na Loja</DialogTitle></DialogHeader>
          {acaoProduto && (
            <form onSubmit={submitEntradaQuick} className="space-y-3">
              <div className="p-3 bg-blue-50 rounded text-sm">
                <p className="font-medium">{acaoProduto.nome}</p>
                <p className="text-gray-600">Saldo atual: <strong>{saldoLojaDe(acaoProduto.id)}</strong></p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="quick-entr-qtd">Quantidade a Adicionar *</Label>
                <Input id="quick-entr-qtd" type="number" min="1" required value={formEntradaQuick.quantidade}
                  onChange={e => setFormEntradaQuick({ ...formEntradaQuick, quantidade: e.target.value })}
                  placeholder="Digite a quantidade" data-testid="quick-entrada-qtd" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="quick-entr-motivo">Motivo (opcional)</Label>
                <Input id="quick-entr-motivo" value={formEntradaQuick.motivo}
                  onChange={e => setFormEntradaQuick({ ...formEntradaQuick, motivo: e.target.value })}
                  placeholder="Ex: Compra de fornecedor" />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={fecharAcao}>Cancelar</Button>
                <Button type="submit" disabled={acaoSubmitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="quick-btn-confirmar-entrada">
                  {acaoSubmitting ? "…" : "Registrar Entrada"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={acaoTipo === "transferir"} onOpenChange={(o) => !o && fecharAcao()}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Transferir para Carro</DialogTitle></DialogHeader>
          {acaoProduto && (
            <form onSubmit={submitTransferQuick} className="space-y-3">
              <div className="p-3 bg-blue-50 rounded text-sm">
                <p className="font-medium">{acaoProduto.nome}</p>
                <p className="text-gray-600">Disponível na loja: <strong>{saldoLojaDe(acaoProduto.id)}</strong></p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="quick-tr-carro">Carro Destino *</Label>
                <select id="quick-tr-carro" required value={formTransferQuick.carro_id}
                  onChange={e => setFormTransferQuick({ ...formTransferQuick, carro_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md" data-testid="quick-transfer-carro">
                  <option value="">Selecione um carro</option>
                  {carros.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="quick-tr-qtd">Quantidade a Transferir *</Label>
                <Input id="quick-tr-qtd" type="number" min="1" max={saldoLojaDe(acaoProduto.id)} required value={formTransferQuick.quantidade}
                  onChange={e => setFormTransferQuick({ ...formTransferQuick, quantidade: e.target.value })}
                  placeholder="Digite a quantidade" data-testid="quick-transfer-qtd" />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={fecharAcao}>Cancelar</Button>
                <Button type="submit" disabled={acaoSubmitting} className="bg-blue-600 hover:bg-blue-700" data-testid="quick-btn-confirmar-transferir">
                  {acaoSubmitting ? "…" : "Transferir"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={acaoTipo === "devolver"} onOpenChange={(o) => !o && fecharAcao()}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Devolver de Carro p/ Loja</DialogTitle></DialogHeader>
          {acaoProduto && (
            <form onSubmit={submitDevolverQuick} className="space-y-3">
              <div className="p-3 bg-orange-50 rounded text-sm">
                <p className="font-medium">{acaoProduto.nome}</p>
                <p className="text-gray-600 mt-1">A devolução retorna o produto do carro para o estoque da loja.</p>
              </div>
              {carrosComSaldoDe(acaoProduto.id).length === 0 ? (
                <p className="text-sm italic text-gray-500 py-3">Nenhum carro tem saldo deste produto.</p>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="quick-dv-carro">Carro de Origem *</Label>
                    <select id="quick-dv-carro" required value={formDevolverQuick.carro_id}
                      onChange={e => setFormDevolverQuick({ ...formDevolverQuick, carro_id: e.target.value, quantidade: "" })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md" data-testid="quick-devolver-carro">
                      <option value="">Selecione um carro com saldo</option>
                      {carrosComSaldoDe(acaoProduto.id).map(c => <option key={c.carro_id} value={c.carro_id}>{c.carro_nome} (saldo {c.quantidade})</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="quick-dv-qtd">Quantidade *</Label>
                    <Input id="quick-dv-qtd" type="number" min="1" max={maxDevolverCarro(formDevolverQuick.carro_id)} required value={formDevolverQuick.quantidade}
                      onChange={e => setFormDevolverQuick({ ...formDevolverQuick, quantidade: e.target.value })}
                      placeholder={formDevolverQuick.carro_id ? `Máx ${maxDevolverCarro(formDevolverQuick.carro_id)}` : "Selecione um carro primeiro"}
                      disabled={!formDevolverQuick.carro_id} data-testid="quick-devolver-qtd" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="quick-dv-motivo">Motivo *</Label>
                    <Input id="quick-dv-motivo" required value={formDevolverQuick.motivo}
                      onChange={e => setFormDevolverQuick({ ...formDevolverQuick, motivo: e.target.value })}
                      placeholder="Ex: Vencido, sobra de rota..." data-testid="quick-devolver-motivo" />
                  </div>
                </>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={fecharAcao}>Cancelar</Button>
                <Button type="submit" disabled={acaoSubmitting || carrosComSaldoDe(acaoProduto.id).length === 0} className="bg-orange-600 hover:bg-orange-700" data-testid="quick-btn-confirmar-devolver">
                  {acaoSubmitting ? "…" : "Devolver"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* FAB Carrinho de Orçamento */}
      {carrinhoOrcamento.length > 0 && (() => {
        const totalCarrinho = carrinhoOrcamento.reduce((sum, it) => sum + (it.quantidade || 0) * (it.preco_custo || 0), 0);
        return (
          <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
            <div className="bg-white border-2 border-fuchsia-500 rounded-lg shadow-lg px-3 py-1.5 text-xs" data-testid="fab-carrinho-total">
              <span className="text-gray-600">Total: </span>
              <span className="font-bold text-fuchsia-700">R$ {totalCarrinho.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <button
              onClick={() => setCarrinhoOpen(true)}
              className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-full shadow-2xl px-5 py-3 flex items-center gap-2 font-semibold transition-transform hover:scale-105"
              data-testid="fab-carrinho"
            >
              <ShoppingCart className="h-5 w-5" />
              Orçamento ({carrinhoOrcamento.length})
            </button>
          </div>
        );
      })()}

      {/* Dialog Carrinho de Orçamento */}
      <Dialog open={carrinhoOpen} onOpenChange={setCarrinhoOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-fuchsia-600" /> Carrinho de Orçamento
            </DialogTitle>
          </DialogHeader>
          {carrinhoOrcamento.length === 0 ? (
            <p className="text-center text-gray-500 py-6">Carrinho vazio. Adicione produtos pelo botão "Novo Pedido".</p>
          ) : (
            <div className="space-y-3">
              <div className="space-y-2 max-h-[55vh] overflow-y-auto">
                {carrinhoOrcamento.map(it => {
                  const subtotal = (it.quantidade || 0) * (it.preco_custo || 0);
                  return (
                    <div key={it.produto_id} className="flex items-center gap-2 p-2 border rounded-md" data-testid={`carrinho-item-${it.produto_id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{it.produto_nome}</p>
                        <p className="text-[11px] text-gray-500">SKU {it.produto_codigo} · custo R$ {Number(it.preco_custo || 0).toFixed(2)}</p>
                      </div>
                      <Input type="number" min="0" value={it.quantidade}
                        onChange={e => atualizarQtdCarrinho(it.produto_id, e.target.value)}
                        className="w-20 h-8 text-center" data-testid={`carrinho-qtd-${it.produto_id}`} />
                      <div className="w-24 text-right text-sm font-semibold text-gray-700">R$ {subtotal.toFixed(2)}</div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removerDoCarrinho(it.produto_id)} className="h-8 w-8 p-0 text-red-600" data-testid={`carrinho-remover-${it.produto_id}`}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-sm font-semibold text-gray-700">Total estimado</span>
                <span className="text-lg font-bold text-fuchsia-700">
                  R$ {carrinhoOrcamento.reduce((sum, it) => sum + (it.quantidade || 0) * (it.preco_custo || 0), 0).toFixed(2)}
                </span>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="orc-data">Data de Entrada Prevista *</Label>
                <Input id="orc-data" type="date" required value={dataEntradaPrevista}
                  onChange={e => setDataEntradaPrevista(e.target.value)} data-testid="orc-data-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="orc-obs">Observações</Label>
                <Input id="orc-obs" value={obsOrcamento} onChange={e => setObsOrcamento(e.target.value)}
                  placeholder="Fornecedor, condição de pagamento etc." />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setCarrinhoOpen(false)}>Continuar Editando</Button>
                <Button type="button" onClick={fecharOrcamento} disabled={fechandoOrcamento} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="btn-fechar-orcamento">
                  {fechandoOrcamento ? "Fechando…" : "Fechar Orçamento"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =========================================
// Aba Pedidos de Reposição (histórico + ações)
// =========================================
function PedidosTab({ pedidos, onAprovar, onCancelar, onDarEntrada, onEditar }) {
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [baixandoPdf, setBaixandoPdf] = useState(null);
  const [editandoPedido, setEditandoPedido] = useState(null);
  const [aprovandoPedido, setAprovandoPedido] = useState(null);
  const filtrados = filtroStatus === "todos" ? pedidos : pedidos.filter(p => p.status === filtroStatus);
  const fmtMoeda = (v) => `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtData = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
  const baixarPdf = async (pedido) => {
    setBaixandoPdf(pedido.id);
    try {
      const response = await axios.get(`${API}/pedidos-reposicao/${pedido.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `pedido_${pedido.numero}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF gerado!");
    } catch { toast.error("Erro ao gerar PDF"); }
    finally { setBaixandoPdf(null); }
  };
  const statusBadge = (status) => {
    const cores = {
      pendente: "bg-amber-100 text-amber-800 border-amber-300",
      aprovado: "bg-blue-100 text-blue-800 border-blue-300",
      concluido: "bg-emerald-100 text-emerald-800 border-emerald-300",
      cancelado: "bg-gray-100 text-gray-600 border-gray-300",
    };
    const labels = { pendente: "Pendente", aprovado: "Aprovado", concluido: "Concluído", cancelado: "Cancelado" };
    return <span className={`text-[11px] px-2 py-0.5 rounded-full border font-bold ${cores[status] || "bg-gray-100"}`}>{labels[status] || status}</span>;
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-fuchsia-600" /> Pedidos de Reposição
          <span className="text-sm text-gray-500 font-normal">({filtrados.length})</span>
        </h3>
        <div className="flex gap-1.5">
          {["todos", "pendente", "aprovado", "concluido", "cancelado"].map(s => (
            <button key={s} onClick={() => setFiltroStatus(s)} data-testid={`filtro-pedido-${s}`}
              className={`text-xs px-2.5 py-1 rounded-full border ${filtroStatus === s ? "bg-fuchsia-600 text-white border-fuchsia-600" : "bg-white text-gray-700 hover:bg-gray-50"}`}>
              {s === "todos" ? "Todos" : (s === "concluido" ? "Concluído" : s.charAt(0).toUpperCase() + s.slice(1))}
            </button>
          ))}
        </div>
      </div>
      {filtrados.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border">
          <ClipboardList className="h-10 w-10 mx-auto text-gray-300" />
          <p className="text-gray-500 mt-3">Nenhum pedido neste filtro.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtrados.map(p => (
            <Card key={p.id} className="overflow-hidden" data-testid={`pedido-card-${p.id}`}>
              <CardContent className="pt-4 space-y-2">
                <div className="flex justify-between items-start gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-base">{p.numero}</p>
                      {statusBadge(p.status)}
                    </div>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      Criado em {fmtData(p.criado_em)} por <strong>{p.criado_por_nome}</strong>
                      {p.data_entrada_prevista && <> · Previsão de entrada: <strong>{p.data_entrada_prevista}</strong></>}
                    </p>
                    {p.aprovado_em && (<p className="text-[11px] text-blue-600">Aprovado em {fmtData(p.aprovado_em)} por {p.aprovado_por_nome}</p>)}
                    {p.concluido_em && (<p className="text-[11px] text-emerald-600">Entrada concluída em {fmtData(p.concluido_em)} por {p.concluido_por_nome}</p>)}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Total estimado</p>
                    <p className="text-lg font-bold text-gray-900">{fmtMoeda(p.total_estimado)}</p>
                  </div>
                </div>
                <details className="text-xs">
                  <summary className="cursor-pointer text-gray-600 font-medium">Ver itens ({(p.itens || []).length})</summary>
                  <ul className="mt-2 space-y-1">
                    {(p.itens || []).map((it, i) => (
                      <li key={i} className="flex justify-between border-b pb-1">
                        <span>{it.quantidade}× {it.produto_nome} <span className="text-gray-400">(SKU {it.produto_codigo})</span></span>
                        <span className="text-gray-600">{fmtMoeda(it.subtotal)}</span>
                      </li>
                    ))}
                  </ul>
                </details>
                {p.observacoes && (<p className="text-[11px] text-gray-500 italic">Obs: {p.observacoes}</p>)}
                <div className="flex justify-end gap-2 pt-1 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => baixarPdf(p)} disabled={baixandoPdf === p.id} className="text-fuchsia-700 border-fuchsia-300 hover:bg-fuchsia-50 gap-1.5" data-testid={`btn-pdf-pedido-${p.id}`}>
                    <FileText className="h-4 w-4" /> {baixandoPdf === p.id ? "Gerando…" : "Imprimir PDF"}
                  </Button>
                  {p.status === "pendente" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setEditandoPedido(p)} className="text-gray-700 border-gray-300 gap-1.5" data-testid={`btn-editar-pedido-${p.id}`}>
                        <Pencil className="h-4 w-4" /> Editar
                      </Button>
                      <Button size="sm" onClick={() => setAprovandoPedido(p)} className="bg-blue-600 hover:bg-blue-700 gap-1.5" data-testid={`btn-aprovar-pedido-${p.id}`}>
                        <Check className="h-4 w-4" /> Aprovar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onCancelar(p.id)} className="text-red-600 border-red-300 gap-1.5" data-testid={`btn-cancelar-pedido-${p.id}`}>
                        <X className="h-4 w-4" /> Cancelar
                      </Button>
                    </>
                  )}
                  {p.status === "aprovado" && (
                    <>
                      <Button size="sm" onClick={() => onDarEntrada(p)} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid={`btn-entrada-pedido-${p.id}`}>
                        <Check className="h-4 w-4" /> Dar Entrada Agora
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onCancelar(p.id)} className="text-red-600 border-red-300 gap-1.5" data-testid={`btn-cancelar-pedido-${p.id}`}>
                        <X className="h-4 w-4" /> Cancelar
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog: Editar Pedido (remover itens / ajustar quantidades) */}
      <Dialog open={!!editandoPedido} onOpenChange={(o) => !o && setEditandoPedido(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Pedido {editandoPedido?.numero}</DialogTitle>
            <DialogDescription>Ajuste quantidades ou remova itens. O total será recalculado automaticamente.</DialogDescription>
          </DialogHeader>
          {editandoPedido && (
            <EditarPedidoForm pedido={editandoPedido} onSalvar={async (itens) => {
              const ok = await onEditar(editandoPedido.id, itens);
              if (ok) setEditandoPedido(null);
            }} />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Aprovar com parcelas → cria despesas */}
      <Dialog open={!!aprovandoPedido} onOpenChange={(o) => !o && setAprovandoPedido(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aprovar Pedido {aprovandoPedido?.numero}</DialogTitle>
            <DialogDescription>Defina a forma de pagamento. Cria despesa(s) no fluxo financeiro como Fornecedor (PJ).</DialogDescription>
          </DialogHeader>
          {aprovandoPedido && (
            <AprovarPedidoForm pedido={aprovandoPedido} onConfirmar={async (payload) => {
              const ok = await onAprovar(aprovandoPedido.id, payload);
              if (ok) setAprovandoPedido(null);
            }} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =========================================
// Form de edição de pedido (itens)
// =========================================
function EditarPedidoForm({ pedido, onSalvar }) {
  const [itens, setItens] = useState((pedido.itens || []).map(it => ({ ...it })));
  const [salvando, setSalvando] = useState(false);
  const total = itens.reduce((s, it) => s + (it.quantidade || 0) * (it.preco_custo || 0), 0);
  const fmt = (v) => `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const updateQtd = (idx, q) => {
    const n = Math.max(0, parseInt(q) || 0);
    setItens(prev => prev.map((it, i) => i === idx ? { ...it, quantidade: n, subtotal: n * (it.preco_custo || 0) } : it));
  };
  const remover = (idx) => setItens(prev => prev.filter((_, i) => i !== idx));
  const submit = async () => {
    const validos = itens.filter(it => it.quantidade > 0);
    if (validos.length === 0) { toast.error("O pedido precisa ter ao menos 1 item com quantidade > 0"); return; }
    setSalvando(true);
    await onSalvar(validos.map(it => ({ produto_id: it.produto_id, quantidade: it.quantidade })));
    setSalvando(false);
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">Ajuste quantidades ou remova itens que o fornecedor não tem em estoque.</p>
      <div className="space-y-2 max-h-[50vh] overflow-y-auto">
        {itens.map((it, idx) => (
          <div key={idx} className="flex items-center gap-2 p-2 border rounded" data-testid={`editar-item-${it.produto_id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{it.produto_nome}</p>
              <p className="text-[11px] text-gray-500">SKU {it.produto_codigo} · {fmt(it.preco_custo)} un.</p>
            </div>
            <Input type="number" min="0" value={it.quantidade} onChange={e => updateQtd(idx, e.target.value)} className="w-20 h-8 text-center" data-testid={`editar-qtd-${it.produto_id}`} />
            <div className="w-24 text-right text-sm font-semibold">{fmt((it.quantidade || 0) * (it.preco_custo || 0))}</div>
            <Button type="button" variant="ghost" size="sm" onClick={() => remover(idx)} className="h-8 w-8 p-0 text-red-600" data-testid={`editar-remover-${it.produto_id}`}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
      <div className="flex justify-between pt-2 border-t">
        <span className="text-sm font-semibold">Novo total</span>
        <span className="text-lg font-bold text-fuchsia-700">{fmt(total)}</span>
      </div>
      <div className="flex justify-end">
        <Button onClick={submit} disabled={salvando} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-salvar-edicao-pedido">
          {salvando ? "Salvando…" : "Salvar Alterações"}
        </Button>
      </div>
    </div>
  );
}

// =========================================
// Form de aprovação com parcelas / pgto único
// =========================================
function AprovarPedidoForm({ pedido, onConfirmar }) {
  const total = Number(pedido.total_estimado || 0);
  const hoje = new Date().toISOString().split("T")[0];
  const dLimite = (() => { const d = new Date(); d.setDate(d.getDate() + 120); return d.toISOString().split("T")[0]; })();
  const [modo, setModo] = useState("unica"); // 'unica' | 'parcelado'
  const [dataUnica, setDataUnica] = useState(hoje);
  const [parcelas, setParcelas] = useState([{ data: hoje, valor: total }]);
  const [fornecedor, setFornecedor] = useState(pedido.fornecedor_pagamento || "");
  const [confirmando, setConfirmando] = useState(false);

  const fmt = (v) => `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const somaParcelas = parcelas.reduce((s, p) => s + Number(p.valor || 0), 0);
  const faltante = +(total - somaParcelas).toFixed(2);

  const addParcela = () => {
    if (faltante <= 0) { toast.info("Valor total já está coberto"); return; }
    const ultima = parcelas[parcelas.length - 1];
    const proxData = (() => {
      try {
        const d = new Date(ultima.data + "T00:00:00");
        d.setMonth(d.getMonth() + 1);
        if (d > new Date(dLimite + "T23:59:59")) return dLimite;
        return d.toISOString().split("T")[0];
      } catch { return hoje; }
    })();
    setParcelas(prev => [...prev, { data: proxData, valor: faltante }]);
  };
  const removerParcela = (idx) => setParcelas(prev => prev.filter((_, i) => i !== idx));
  const updateParcela = (idx, campo, valor) => setParcelas(prev => prev.map((p, i) => i === idx ? { ...p, [campo]: campo === "valor" ? Math.max(0, parseFloat(valor) || 0) : valor } : p));

  const submit = async () => {
    let payload = { fornecedor };
    if (modo === "unica") {
      if (!dataUnica) { toast.error("Defina a data de pagamento"); return; }
      if (dataUnica > dLimite) { toast.error("Data não pode exceder 120 dias"); return; }
      if (dataUnica < hoje) { toast.error("Data não pode ser anterior a hoje"); return; }
      payload.parcelas = [{ data: dataUnica, valor: total }];
    } else {
      if (Math.abs(somaParcelas - total) > 0.01) { toast.error(`Soma das parcelas (${fmt(somaParcelas)}) precisa ser igual ao total (${fmt(total)})`); return; }
      for (const p of parcelas) {
        if (!p.data) { toast.error("Defina a data de cada parcela"); return; }
        if (p.data < hoje) { toast.error(`Data ${p.data} é anterior a hoje`); return; }
        if (p.data > dLimite) { toast.error(`Data ${p.data} excede 120 dias`); return; }
        if (p.valor <= 0) { toast.error("Cada parcela precisa ter valor > 0"); return; }
      }
      payload.parcelas = parcelas;
    }
    setConfirmando(true);
    await onConfirmar(payload);
    setConfirmando(false);
  };

  return (
    <div className="space-y-3">
      <div className="bg-blue-50 border border-blue-200 rounded p-3 flex justify-between items-center">
        <div>
          <p className="text-xs text-blue-700">Total do pedido</p>
          <p className="text-2xl font-bold text-blue-900">{fmt(total)}</p>
        </div>
        <p className="text-xs text-blue-700">Aprovação cria despesa(s) no fluxo financeiro como Fornecedor (PJ).</p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="aprov-forn">Fornecedor</Label>
        <Input id="aprov-forn" value={fornecedor} onChange={e => setFornecedor(e.target.value)} placeholder={`Ex: ${pedido.numero}`} data-testid="aprov-fornecedor-input" />
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={() => setModo("unica")} className={`flex-1 px-3 py-2 rounded border-2 text-sm font-medium ${modo === "unica" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-gray-200 text-gray-600"}`} data-testid="aprov-modo-unica">Pagamento Único</button>
        <button type="button" onClick={() => setModo("parcelado")} className={`flex-1 px-3 py-2 rounded border-2 text-sm font-medium ${modo === "parcelado" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-gray-200 text-gray-600"}`} data-testid="aprov-modo-parcelado">Parcelado</button>
      </div>
      {modo === "unica" ? (
        <div className="space-y-1.5">
          <Label htmlFor="aprov-data">Data do Pagamento *</Label>
          <Input id="aprov-data" type="date" value={dataUnica} min={hoje} max={dLimite} onChange={e => setDataUnica(e.target.value)} required data-testid="aprov-data-unica" />
          <p className="text-[11px] text-gray-500">Limite: até {dLimite.split("-").reverse().join("/")} (120 dias a partir de hoje).</p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="space-y-2 max-h-[40vh] overflow-y-auto">
            {parcelas.map((p, idx) => (
              <div key={idx} className="grid grid-cols-[1fr,1fr,auto] gap-2 items-end border rounded p-2" data-testid={`aprov-parcela-${idx}`}>
                <div>
                  <Label className="text-[11px]">Parcela {idx + 1} — Data</Label>
                  <Input type="date" value={p.data} min={hoje} max={dLimite} onChange={e => updateParcela(idx, "data", e.target.value)} data-testid={`aprov-parcela-data-${idx}`} />
                </div>
                <div>
                  <Label className="text-[11px]">Valor (R$)</Label>
                  <Input type="number" step="0.01" min="0" value={p.valor} onChange={e => updateParcela(idx, "valor", e.target.value)} data-testid={`aprov-parcela-valor-${idx}`} />
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => removerParcela(idx)} disabled={parcelas.length === 1} className="h-9 w-9 p-0 text-red-600">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          <div className="flex justify-between items-center text-sm bg-gray-50 rounded p-2">
            <span>Soma das parcelas: <strong className={Math.abs(somaParcelas - total) <= 0.01 ? "text-emerald-700" : "text-red-700"}>{fmt(somaParcelas)}</strong></span>
            <span>Faltante: <strong className={faltante <= 0.01 ? "text-emerald-700" : "text-amber-700"}>{fmt(Math.max(0, faltante))}</strong></span>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addParcela} disabled={faltante <= 0.01} className="w-full gap-1.5 text-blue-700 border-blue-300" data-testid="btn-add-parcela">
            <Plus className="h-4 w-4" /> Nova Parcela ({fmt(Math.max(0, faltante))} restante)
          </Button>
          <p className="text-[11px] text-gray-500">Cada parcela vira uma despesa em "Contas a Pagar". Limite: 120 dias a partir de hoje.</p>
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button onClick={submit} disabled={confirmando} className="bg-blue-600 hover:bg-blue-700 gap-1.5" data-testid="btn-confirmar-aprovacao">
          <Check className="h-4 w-4" /> {confirmando ? "Aprovando…" : "Confirmar Aprovação"}
        </Button>
      </div>
    </div>
  );
}

// =========================================
// Aba Loja Online: foto + descrição comercial
// =========================================
function LojaTab({ produto, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const [desc, setDesc] = useState(produto.descricao_loja || "");
  const [salvandoDesc, setSalvandoDesc] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setDesc(produto.descricao_loja || ""); }, [produto.id, produto.descricao_loja]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await axios.post(`${API}/produtos/${produto.id}/imagem`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto atualizada!");
      onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao enviar foto"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removerFoto = async () => {
    if (!window.confirm("Remover foto deste produto?")) return;
    try {
      await axios.delete(`${API}/produtos/${produto.id}/imagem`);
      toast.success("Foto removida");
      onChanged?.();
    } catch { toast.error("Erro ao remover"); }
  };

  const salvarDesc = async () => {
    setSalvandoDesc(true);
    try {
      await axios.patch(`${API}/produtos/${produto.id}/loja`, { descricao_loja: desc });
      toast.success("Descrição salva");
      onChanged?.();
    } catch { toast.error("Erro"); }
    finally { setSalvandoDesc(false); }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-[200px,1fr] gap-5">
        <div>
          <Label className="text-sm font-semibold">Foto do Produto</Label>
          <div className="mt-2 w-48 h-48 rounded-md bg-gray-100 border relative flex items-center justify-center overflow-hidden">
            {produto.imagem_url ? (
              <img src={produto.imagem_url} alt={produto.nome} className="w-full h-full object-cover" data-testid="loja-foto-preview" />
            ) : (
              <ImageIcon className="h-12 w-12 text-gray-300" />
            )}
          </div>
          <div className="flex gap-2 mt-3">
            <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-md text-sm" data-testid="btn-upload-foto">
              <input type="file" accept="image/*" hidden onChange={handleUpload} ref={fileRef} />
              <Upload className="h-4 w-4" /> {uploading ? "Enviando…" : "Enviar foto"}
            </label>
            {produto.imagem_url && (
              <Button type="button" variant="outline" size="sm" onClick={removerFoto} className="gap-1.5 text-red-600 border-red-300" data-testid="btn-remover-foto">
                <Trash2 className="h-4 w-4" /> Remover
              </Button>
            )}
          </div>
          <p className="text-[11px] text-gray-500 mt-2">Aparece na sua Loja Online e no app público.</p>
        </div>
        <div>
          <Label htmlFor="descricao-loja" className="text-sm font-semibold">Descrição Comercial</Label>
          <textarea id="descricao-loja" rows={6} value={desc} onChange={e => setDesc(e.target.value)}
            placeholder="Descrição que aparece no card do produto da Loja Online (opcional)"
            className="mt-2 w-full text-sm border rounded-md p-2.5 resize-y"
            data-testid="loja-descricao-textarea" />
          <div className="flex justify-end mt-2">
            <Button type="button" onClick={salvarDesc} disabled={salvandoDesc} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-salvar-descricao">
              {salvandoDesc ? "Salvando…" : "Salvar Descrição"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =========================================
// Aba Estoque: saldos + Entrada / Transferir / Devolver
// =========================================
function EstoqueTab({ produto, onChanged }) {
  const [estoque, setEstoque] = useState({ saldo_loja: 0, carros: [] });
  const [carros, setCarros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogEntrada, setDialogEntrada] = useState(false);
  const [dialogTransfer, setDialogTransfer] = useState(false);
  const [dialogDevolver, setDialogDevolver] = useState(false);
  const [formEntrada, setFormEntrada] = useState({ quantidade: "", motivo: "" });
  const [formTransfer, setFormTransfer] = useState({ quantidade: "", carro_id: "" });
  const [formDevolver, setFormDevolver] = useState({ quantidade: "", carro_id: "", motivo: "" });
  const [submitting, setSubmitting] = useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const [estRes, carrosRes] = await Promise.all([
        axios.get(`${API}/estoques/produto/${produto.id}`),
        axios.get(`${API}/carros`),
      ]);
      setEstoque(estRes.data);
      setCarros(carrosRes.data || []);
    } catch (e) {
      toast.error("Erro ao carregar estoque");
    } finally { setLoading(false); }
  };
  useEffect(() => { carregar(); }, [produto.id]);

  const submitEntrada = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/movimentacoes`, {
        produto_id: produto.id,
        tipo: "entrada_loja",
        quantidade: parseInt(formEntrada.quantidade),
        motivo: formEntrada.motivo || "Reposição",
      });
      toast.success("Entrada registrada!");
      setDialogEntrada(false);
      setFormEntrada({ quantidade: "", motivo: "" });
      await carregar();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao registrar entrada");
    } finally { setSubmitting(false); }
  };

  const submitTransfer = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/movimentacoes`, {
        produto_id: produto.id,
        tipo: "transferencia",
        quantidade: parseInt(formTransfer.quantidade),
        carro_id_destino: formTransfer.carro_id,
        motivo: "Transferência para carro",
      });
      toast.success("Transferência realizada!");
      setDialogTransfer(false);
      setFormTransfer({ quantidade: "", carro_id: "" });
      await carregar();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao transferir");
    } finally { setSubmitting(false); }
  };

  const submitDevolver = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/movimentacoes`, {
        produto_id: produto.id,
        tipo: "devolucao_carro",
        quantidade: parseInt(formDevolver.quantidade),
        carro_id_origem: formDevolver.carro_id,
        motivo: formDevolver.motivo || "Devolução do carro para loja",
      });
      toast.success("Devolução realizada!");
      setDialogDevolver(false);
      setFormDevolver({ quantidade: "", carro_id: "", motivo: "" });
      await carregar();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao devolver");
    } finally { setSubmitting(false); }
  };

  const carrosComSaldo = estoque.carros.filter(c => c.quantidade > 0);
  const maxDevolver = (carroId) => estoque.carros.find(c => c.carro_id === carroId)?.quantidade || 0;

  return (
    <div className="space-y-4" data-testid="estoque-tab-content">
      {/* Resumo da Loja */}
      <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200">
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Warehouse className="h-8 w-8 text-cyan-600" />
              <div>
                <p className="text-xs text-cyan-700 font-semibold uppercase">Saldo na Loja</p>
                <p className="text-3xl font-bold text-cyan-900" data-testid="saldo-loja">{loading ? "…" : estoque.saldo_loja}</p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" onClick={() => setDialogEntrada(true)} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="btn-acao-entrada">
                <TrendingUp className="h-4 w-4" /> Entrada
              </Button>
              <Button size="sm" onClick={() => setDialogTransfer(true)} disabled={estoque.saldo_loja <= 0} className="gap-1.5 bg-blue-600 hover:bg-blue-700" data-testid="btn-acao-transferir">
                <Send className="h-4 w-4" /> Transferir
              </Button>
              <Button size="sm" onClick={() => setDialogDevolver(true)} disabled={carrosComSaldo.length === 0} variant="outline" className="gap-1.5 text-orange-700 border-orange-300 hover:bg-orange-50" data-testid="btn-acao-devolver">
                <RotateCcw className="h-4 w-4" /> Devolver
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Saldos por Carro */}
      <div>
        <p className="text-sm font-semibold mb-2 text-gray-700">Saldo nos Carros</p>
        {loading ? (
          <p className="text-sm text-gray-500">Carregando…</p>
        ) : estoque.carros.length === 0 ? (
          <p className="text-sm text-gray-500 italic">Nenhum carro cadastrado.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {estoque.carros.map(c => (
              <div key={c.carro_id} className={`border rounded-md px-3 py-2 ${c.quantidade > 0 ? 'bg-white' : 'bg-gray-50 text-gray-400'}`} data-testid={`saldo-carro-${c.carro_id}`}>
                <p className="text-[11px] text-gray-500 truncate">{c.carro_nome}</p>
                <p className={`text-lg font-bold ${c.quantidade > 0 ? 'text-indigo-700' : 'text-gray-400'}`}>{c.quantidade}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dialog Entrada */}
      <Dialog open={dialogEntrada} onOpenChange={setDialogEntrada}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Registrar Entrada na Loja</DialogTitle></DialogHeader>
          <form onSubmit={submitEntrada} className="space-y-3">
            <div className="p-3 bg-blue-50 rounded text-sm">
              <p className="font-medium">{produto.nome}</p>
              <p className="text-gray-600">Saldo atual: <strong>{estoque.saldo_loja}</strong></p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="entr-qtd">Quantidade a Adicionar *</Label>
              <Input id="entr-qtd" type="number" min="1" required value={formEntrada.quantidade}
                onChange={e => setFormEntrada({ ...formEntrada, quantidade: e.target.value })}
                placeholder="Digite a quantidade" data-testid="entrada-qtd-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="entr-motivo">Motivo (opcional)</Label>
              <Input id="entr-motivo" value={formEntrada.motivo}
                onChange={e => setFormEntrada({ ...formEntrada, motivo: e.target.value })}
                placeholder="Ex: Compra de fornecedor" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogEntrada(false)}>Cancelar</Button>
              <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-confirmar-entrada">
                {submitting ? "…" : "Registrar Entrada"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Transferir */}
      <Dialog open={dialogTransfer} onOpenChange={setDialogTransfer}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Transferir para Carro</DialogTitle></DialogHeader>
          <form onSubmit={submitTransfer} className="space-y-3">
            <div className="p-3 bg-blue-50 rounded text-sm">
              <p className="font-medium">{produto.nome}</p>
              <p className="text-gray-600">Disponível na loja: <strong>{estoque.saldo_loja}</strong></p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tr-carro">Carro Destino *</Label>
              <select id="tr-carro" required value={formTransfer.carro_id}
                onChange={e => setFormTransfer({ ...formTransfer, carro_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md" data-testid="transfer-carro-select">
                <option value="">Selecione um carro</option>
                {carros.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tr-qtd">Quantidade a Transferir *</Label>
              <Input id="tr-qtd" type="number" min="1" max={estoque.saldo_loja} required value={formTransfer.quantidade}
                onChange={e => setFormTransfer({ ...formTransfer, quantidade: e.target.value })}
                placeholder="Digite a quantidade" data-testid="transfer-qtd-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogTransfer(false)}>Cancelar</Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-confirmar-transferir">
                {submitting ? "…" : "Transferir"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Devolver */}
      <Dialog open={dialogDevolver} onOpenChange={setDialogDevolver}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Devolver de Carro p/ Loja</DialogTitle></DialogHeader>
          <form onSubmit={submitDevolver} className="space-y-3">
            <div className="p-3 bg-orange-50 rounded text-sm">
              <p className="font-medium">{produto.nome}</p>
              <p className="text-gray-600 mt-1">A devolução retorna o produto do carro para o estoque da loja.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dv-carro">Carro de Origem *</Label>
              <select id="dv-carro" required value={formDevolver.carro_id}
                onChange={e => setFormDevolver({ ...formDevolver, carro_id: e.target.value, quantidade: "" })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md" data-testid="devolver-carro-select">
                <option value="">Selecione um carro com saldo</option>
                {carrosComSaldo.map(c => <option key={c.carro_id} value={c.carro_id}>{c.carro_nome} (saldo {c.quantidade})</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dv-qtd">Quantidade *</Label>
              <Input id="dv-qtd" type="number" min="1" max={maxDevolver(formDevolver.carro_id)} required value={formDevolver.quantidade}
                onChange={e => setFormDevolver({ ...formDevolver, quantidade: e.target.value })}
                placeholder={formDevolver.carro_id ? `Máx ${maxDevolver(formDevolver.carro_id)}` : "Selecione um carro primeiro"}
                disabled={!formDevolver.carro_id} data-testid="devolver-qtd-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dv-motivo">Motivo *</Label>
              <Input id="dv-motivo" required value={formDevolver.motivo}
                onChange={e => setFormDevolver({ ...formDevolver, motivo: e.target.value })}
                placeholder="Ex: Vencido, sobra de rota..." />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogDevolver(false)}>Cancelar</Button>
              <Button type="submit" disabled={submitting} className="bg-orange-600 hover:bg-orange-700" data-testid="btn-confirmar-devolver">
                {submitting ? "…" : "Devolver"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
